import SrtParser, {Line} from "@qgustavor/srt-parser/src";
import {downloadTextFile} from "@/utils/fileUtils";
import {defaultSubtitleStyle, Subtitle} from "@/models/Subtitle";
import {calculateProportionalFontSize} from "@/models/SubtitleStyle";

export function timestampToMilliseconds(timestamp: string): number {
    const parts = timestamp.split(':');
    if (parts.length !== 3) {
        throw new Error('Invalid timestamp format');
    }

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const [seconds, milliseconds] = parts[2].split(',').map(val => parseInt(val, 10));

    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds) || isNaN(milliseconds)) {
        throw new Error('Invalid timestamp format');
    }

    return hours * 3600000 + minutes * 60000 + seconds * 1000 + milliseconds;
}

export function secondsToMs(seconds: number): number {
    return Math.round(seconds * 1000);
}

export const millisecondsToTimestamp = (ms: number): string => {
    return SrtParser.prototype.secondsToTimestamp(ms);
};

export function subtitlesToSrt(subtitles: Subtitle[]): string {
    return subtitles
        .filter(el => !el.deleted)
        .sort((a, b) => a.startTimeMs - b.startTimeMs)
        .map((subtitle, idx) => {
            const id = idx + 1;
            const text = subtitle.text.split('\n')
                .map((line: string) => line.trim())
                .filter((line: string) => line.length > 0)
                .join('\n');
            return `${id}\n${millisecondsToTimestamp(subtitle.startTimeMs)} --> ${millisecondsToTimestamp(subtitle.endTimeMs)}\n${text}\n\n`
        }).join('').trim();
}

export function findActiveSubtitleIndices(subtitles: Subtitle[], timeInSeconds: number, leadTimeSeconds = 0): number[] {
    const timeMs = secondsToMs(timeInSeconds);
    const leadMs = secondsToMs(leadTimeSeconds);
    const result: number[] = [];
    for (let i = 0; i < subtitles.length; i++) {
        const item = subtitles[i];
        if (!item.deleted && timeMs >= item.startTimeMs - leadMs && timeMs < item.endTimeMs) {
            result.push(i);
        }
    }
    return result;
}

export interface LaneInfo {
    lane: number;
    laneCount: number;
    nextSameLaneStart?: number;
}

/**
 * Assign each (non-deleted) subtitle a lane so that temporally overlapping cues stack
 * into separate rows. Items are grouped into connected overlap runs; within a group,
 * lanes are assigned greedily (first lane whose last item has already ended). `laneCount`
 * is per-group, so regions without any overlap stay a single full-height lane.
 * `nextSameLaneStart` is the start time (seconds) of the following cue in the same lane,
 * so renderers can cap a cue's minimum-width box before its lane neighbor begins.
 */
export function assignLanes(subtitles: Subtitle[]): Map<string, LaneInfo> {
    const result = new Map<string, LaneInfo>();
    const sorted = subtitles
        .filter(s => !s.deleted)
        .sort((a, b) => a.startTimeMs - b.startTimeMs || a.endTimeMs - b.endTimeMs);

    let group: LaneInfo[] = [];
    let laneEnds: number[] = [];
    let groupMaxEnd = -Infinity;
    const lastInLane: LaneInfo[] = [];

    const flush = () => {
        for (const info of group) {
            info.laneCount = laneEnds.length;
        }
        group = [];
        laneEnds = [];
        groupMaxEnd = -Infinity;
    };

    for (const item of sorted) {
        if (group.length > 0 && item.startTimeMs >= groupMaxEnd) {
            flush();
        }
        let lane = laneEnds.findIndex(end => end <= item.startTimeMs);
        if (lane === -1) {
            lane = laneEnds.length;
            laneEnds.push(item.endTimeMs);
        } else {
            laneEnds[lane] = item.endTimeMs;
        }
        const info: LaneInfo = {lane, laneCount: 1};
        const prev = lastInLane[lane];
        if (prev) {
            prev.nextSameLaneStart = item.startTimeSeconds;
        }
        lastInLane[lane] = info;
        result.set(item.id, info);
        group.push(info);
        groupMaxEnd = Math.max(groupMaxEnd, item.endTimeMs);
    }
    flush();

    return result;
}

// Mirrored by AssExporter::computeStackOffsets in app/Services/AssExporter.php — the
// editor preview and the burned output must stack concurrent cues identically.
const STACK_LINE_HEIGHT_RATIO = 1.3;
const STACK_LANE_GAP_RATIO = 0.4;
const STACK_MAX_OFFSET_PERCENT = 80;

interface StackCue {
    id: string;
    start: number;
    end: number;
    height: number;
}

/**
 * Vertical offsets (percent of video height) separating concurrent cues that share
 * the same effective anchor (explicit style position or the default placement), so
 * they don't render on top of each other. Per anchor, connected overlap runs get
 * greedy lanes in start order; the last lane keeps the anchor position and earlier
 * lanes stack away from the nearer screen edge, first-started outermost. Adjacent
 * lanes clear half of each other's height plus a gap, since positions are
 * center-anchored. Subtract the offset from the cue's centerY to place it.
 */
export function computeStackOffsets(subtitles: Subtitle[], videoHeight: number): Map<string, number> {
    const offsets = new Map<string, number>();
    if (videoHeight <= 0) {
        return offsets;
    }

    const defaultFontSize = calculateProportionalFontSize(videoHeight);
    const gap = STACK_LANE_GAP_RATIO * defaultFontSize;

    const partitions = new Map<string, {anchorY: number; cues: StackCue[]}>();
    for (const s of subtitles) {
        if (s.deleted || !s.text) {
            continue;
        }
        const anchorX = s.style?.centerX ?? defaultSubtitleStyle.centerX!;
        const anchorY = s.style?.centerY ?? defaultSubtitleStyle.centerY!;
        const key = `${anchorX}:${anchorY}`;
        if (!partitions.has(key)) {
            partitions.set(key, {anchorY, cues: []});
        }
        partitions.get(key)!.cues.push({
            id: s.id,
            start: s.startTimeMs,
            end: s.endTimeMs,
            height: s.text.split('\n').length * (s.style?.fontSize ?? defaultFontSize) * STACK_LINE_HEIGHT_RATIO,
        });
    }

    for (const {anchorY, cues} of partitions.values()) {
        if (cues.length < 2) {
            continue;
        }
        const direction = anchorY >= 50 ? 1 : -1;
        cues.sort((a, b) => a.start - b.start || a.end - b.end);

        let group: StackCue[] = [];
        let groupMaxEnd = -Infinity;

        const flush = () => {
            if (group.length > 1) {
                const lanes = new Map<string, number>();
                const laneEnds: number[] = [];
                const laneHeights: number[] = [];
                for (const cue of group) {
                    let lane = laneEnds.findIndex(end => end <= cue.start);
                    if (lane === -1) {
                        lane = laneEnds.length;
                    }
                    laneEnds[lane] = cue.end;
                    laneHeights[lane] = Math.max(laneHeights[lane] ?? 0, cue.height);
                    lanes.set(cue.id, lane);
                }

                const laneOffsets: number[] = [];
                laneOffsets[laneHeights.length - 1] = 0;
                for (let lane = laneHeights.length - 2; lane >= 0; lane--) {
                    laneOffsets[lane] = laneOffsets[lane + 1] + (laneHeights[lane + 1] + laneHeights[lane]) / 2 + gap;
                }

                for (const cue of group) {
                    const offsetPx = laneOffsets[lanes.get(cue.id)!];
                    if (offsetPx > 0) {
                        offsets.set(cue.id, direction * Math.min((offsetPx / videoHeight) * 100, STACK_MAX_OFFSET_PERCENT));
                    }
                }
            }
            group = [];
            groupMaxEnd = -Infinity;
        };

        for (const cue of cues) {
            if (group.length > 0 && cue.start >= groupMaxEnd) {
                flush();
            }
            group.push(cue);
            groupMaxEnd = Math.max(groupMaxEnd, cue.end);
        }
        flush();
    }

    return offsets;
}

/**
 * Linear scan on purpose: with overlapping cues allowed, items can be dragged past
 * their neighbors, so the array is neither sorted nor binary-searchable. When several
 * cues contain the time, the most recently started one wins, so selection lands on the
 * current line rather than a long background cue.
 */
export function findClosestSubtitleIndex(subtitles: Subtitle[], timeInSeconds: number): number {
    const timeMs = secondsToMs(timeInSeconds);

    let containingIdx = -1;
    let containingStartMs = -Infinity;
    let closestIdx = 0;
    let minDiff = Number.MAX_VALUE;

    for (let i = 0; i < subtitles.length; i++) {
        const item = subtitles[i];
        if (item.deleted) {
            continue;
        }

        if (timeMs >= item.startTimeMs && timeMs < item.endTimeMs) {
            if (item.startTimeMs > containingStartMs) {
                containingIdx = i;
                containingStartMs = item.startTimeMs;
            }
            continue;
        }

        let diff = Math.abs(item.startTimeMs - timeMs);
        if (timeMs < item.endTimeMs) {
            diff = Math.min(diff, Math.abs(item.endTimeMs - timeMs));
        }

        if (diff < minDiff) {
            minDiff = diff;
            closestIdx = i;
        }
    }

    return containingIdx !== -1 ? containingIdx : closestIdx;
}
